import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { IonIcon, ModalController, LoadingController, NavController } from '@ionic/angular/standalone';
import { Order } from '@chemist2u/types-client/C2U/ParseObjects';
import { addIcons } from 'ionicons';
import { accessibilityOutline, arrowForwardOutline, receiptOutline, warning } from 'ionicons/icons';
import { StatusTagComponent } from '../status-tag/status-tag.component';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { ButtonComponent } from '../../primitives/button/button.component';
import { OrderCardTimelineComponent } from '../order-card-timeline/order-card-timeline.component';
import { TOrderFulfillmentMethod, TOrderFulfillmentMethodSameDay, TOrderFulfillmentMethodStandard, TOrderStatus } from '@chemist2u/types-client/C2U/Interfaces';
import { ClickAndCollectPage } from 'src/app/pages/click-and-collect/click-and-collect.page';
import { TimeService } from 'src/app/services/time.service';
import { CountdownComponent } from '../countdown/countdown.component';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { CloudFunctionsService } from 'src/app/services/cloud.functions.service';
import { ErrorService } from 'src/app/services/error.service';
import { StateService } from 'src/app/services/state.service';
import { Router } from '@angular/router';

type TTimelineRow = { title: string, description: string };
type TStep = "CHECK_SCRIPT" | "PHARMACIST_REVIEW" | "PAY" | "COLLECTION" | "DELIVERY" | "AWAITING_SCRIPT";

const STEPS = new Map<TStep, TTimelineRow>([
  ["CHECK_SCRIPT", {
    title: "We check your scripts",
    description: "We verify your script and confirm the price.",
  }],
  ["PAY", {
    title: "Make payment",
    description: "We'll send a payment link to your phone.",
  }],
  ["AWAITING_SCRIPT", {
    title: "Awaiting script",
    description: "We're waiting for your script.",
  }],
  ["PHARMACIST_REVIEW", {
    title: "Pharmacist review",
    description: "A pharmacist is reviewing your order and checking stock.",
  }],
  ["COLLECTION", {
    title: "Collection",
    description: "You’ll receive a message from us when your order is ready from your local pharmacy.",
  }],
  ["DELIVERY", {
    title: "Delivery",
    description: "You'll receive a message when your order is on the road.",
  }],
]);

@Component({
    selector: 'app-order-card',
    templateUrl: './order-card.component.html',
    styleUrls: ['./order-card.component.scss'],
    providers: [CurrencyPipe],
    imports: [CommonModule, StatusTagComponent, ProgressBarComponent, OrderCardTimelineComponent, ButtonComponent, IonIcon, CountdownComponent]
})
export class OrderCardComponent {
  private $state = inject(StateService);
  private $time = inject(TimeService);
  private $amplitude = inject(AmplitudeService);
  private $error = inject(ErrorService);
  private $cloud = inject(CloudFunctionsService);
  private modalController = inject(ModalController);
  private currencyPipe = inject(CurrencyPipe);
  private loadingController = inject(LoadingController);
  private navController = inject(NavController);
  private router = inject(Router);
  public mode = input<"collapsed" | "regular" | "expanded">("expanded");
  public order = input.required<Order>();
  public hasViewOrderInp = input<boolean>(true, {
    alias: 'hasViewOrderButton',
  });

  public isPaying = signal<boolean>(false);

  constructor() {
    addIcons({ accessibilityOutline, arrowForwardOutline, receiptOutline, warning });
  }

  public fulfillmentMethod = computed<TOrderFulfillmentMethod | undefined>(() => {
    return this.order().fulfillmentDetails || undefined;
  });

  public orderStatus = computed<TOrderStatus>(() => {
    const order = this.order();
    const defaultStatus = "Pending";
    return order ? order.status || defaultStatus : defaultStatus;
  });

  public orderStatusTag = computed<TOrderStatus | "Ready For Collection" | "Collected">(() => {
    return this.orderStatus() == 'Ready' && this.isClickAndCollect() ? 'Ready For Collection' :
      this.orderStatus() == 'Delivered' && this.isClickAndCollect() ? 'Collected' :
      this.orderStatus();
  });

  public isClickAndCollect = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method == "clickAndCollect";
  });

  public isOnDemand = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method == "OnDemand";
  });

  public isStandard = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method == "Standard";
  });

  public isSameDay = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method == "SameDay";
  });

  public isPostal = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method == "Postal";
  });

  public isPostalTemperatureControlled = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method === "PostalTemperatureControlled";
  });

  public isSameDayTemperatureControlled = computed<boolean>(() => {
    return this.order().fulfillmentDetails?.selectedMethod.method === "SameDayTemperatureControlled";
  });

  public paymentButtonLabel = computed<string>(() => {
    const order = this.order();
    if (!order) return "Pay";
    return "Pay: " + this.currencyPipe.transform(order.total!, 'USD', 'symbol', '1.2-2') as string;
  });

  public title = computed<string>(() => {
    return "Order " + this.order().orderID;
  });

  public numItems = computed<string>(() => {
    const num = this.order().allItems || 0;
    let str = num + " item";
    if (num != 1) str += "s";
    return str;
  });

  // -- Progress Bar Section

  public withProgressBar = computed<boolean>(() => {
    const isDone = ["Delivered", "Ready", "Completed", "Cancelled"].includes(this.orderStatus());
    return !isDone;
  });

  // -- Program Section

  public paymentError = computed<boolean>(() => this.isProgramWithFailedPayment());

  public isProgramOrder = computed<boolean>(() => {
    return this.order().type == "subscription";
  });

  public isProgramWithFailedPayment = computed<boolean>(() => {
    return this.isProgramOrder() && this.orderStatus() == "Approved";
  });

  public onManageProgram(event: MouseEvent) {
    event.stopPropagation();
    // TODO: fix this on subscription refactor, this only works because a user right now can only have 1 subscription
    const subscription = this.$state.bSubscriptions.getValue()[0];
    const returnURL = this.router.url;
    this.navController.navigateForward(`/program/${subscription.id}`, { queryParams: { returnURL }, state: { subscription }});
  }

  // -- Delivery Section
  
  public withDelivery = computed<boolean>(() => {
    return this.mode() != "collapsed";
  });

  deliveryLabel = computed<string>(() => {
    const order = this.order();
    const isClickAndCollect = this.isClickAndCollect();
    if (order.status == "Delivered") {
      return isClickAndCollect ? "Collected at" : "Delivered at";
    } else {
      return isClickAndCollect ? "Pharmacy hours" : "Delivery window";
    }
  });

  deliveryValue = computed<string>(() => {
    const order = this.order();
    
    if (order.status == "Delivered") return this.$time.formatDate(order.completedAt || order.updatedAt);
    
    if (this.isClickAndCollect()) {
      const pharmacy = order.pharmacy;
      const openingHours = pharmacy?.openingHours;
      if (!openingHours) return "Unknown";
      return this.$time.formatPharmacyHours(openingHours);
    }

    if (this.isOnDemand()) {
      if (order.allocatedShift) {
        return this.$time.formatShiftLabel(order.allocatedShift);
      }
    }

    if (this.isStandard() || this.isSameDay()) {
      const expectedDeliveryDate = (order.fulfillmentDetails as TOrderFulfillmentMethodStandard | TOrderFulfillmentMethodSameDay).expectedDeliveryDate;
      if (expectedDeliveryDate) {
        return this.isStandard()
          ? this.$time.formatStandardDeliveryLabel(expectedDeliveryDate)
          : this.$time.formatSameDayDeliveryLabel(expectedDeliveryDate);
      }
    }

    if (this.isSameDayTemperatureControlled()) {
      const isPaid = !["Pending", "Unpaid", "Approved"].includes(order.status!);
      if (isPaid) {
        return "6pm-9pm (Mon-Fri) excl public holidays.";
      }
      else {
        return "6pm-9pm if paid by 2pm (Mon-Fri) excl public holidays.";
      }
    }
    
    if (this.isPostal()) {
      const isPaid = !["Pending", "Unpaid", "Approved"].includes(order.status!);
      if (isPaid) {
        return "1-2 business days";
      } else {
        return "Express post 1-2 business days if paid by midday.";
      }
    }

    if (this.isPostalTemperatureControlled()) {
      const isPaid = !["Pending", "Unpaid", "Approved"].includes(order.status!);
      if (isPaid) {
        return "1-2 business days";
      } else {
        return "Express post 1-2 business days if paid by midday.";
      }
    }

    return "Please contact support team";
  });

  public clickDelivery = output();

  onClickDelivery() {
    this.clickDelivery.emit();
  }

  // -- Timeline Section
  
  public withTimeline = computed<boolean>(() => {
    return this.mode() == "expanded" && this.timelineRows().length > 0;
  });
  public timelineTitle = computed<string>(() => {
    return "Next steps";
  });

  public timelineRows = computed<TTimelineRow[]>(() => {
    const order = this.order();
    if (order == undefined) return [];

    const status = this.orderStatus();

    const isDone = ["Delivered", "Ready", "Completed", "Cancelled"].includes(status);
    if (isDone) return [];

    const returnValue: TTimelineRow[] = [];

    const needsApproval = ["Unpaid"].includes(status);
    const pendingPaymentDone = ["Pending"].includes(status);
    const needsPayment = needsApproval || ["Approved"].includes(status);
    const needsPharmacyReview = needsPayment || ["Paid"].includes(status);
    const needsDeliveryOrCollection = needsPharmacyReview || ["Pharmacy Approved"].includes(status);
    const needsDelivery = !this.isClickAndCollect() && needsDeliveryOrCollection;
    const needsCollection = this.isClickAndCollect() && needsDeliveryOrCollection;

    if (needsApproval) {
      returnValue.push(STEPS.get("CHECK_SCRIPT")!);
    }
    if (needsPayment) {
      returnValue.push(STEPS.get("PAY")!);
    }
    if (pendingPaymentDone) {
      returnValue.push(STEPS.get("AWAITING_SCRIPT")!);
    }
    if (needsPharmacyReview) {
      returnValue.push(STEPS.get("PHARMACIST_REVIEW")!);
    }
    if (needsDelivery) {
      returnValue.push(STEPS.get("DELIVERY")!);
    } else if (needsCollection) {
      returnValue.push(STEPS.get("COLLECTION")!);
    }

    return returnValue;
  });

  // -- Action Section
  
  public hasViewOrder = computed<boolean>(() => {
    return this.withTimeline() && this.hasViewOrderInp();
  });
  public clickViewOrder = output();

  onClickViewOrder() {
    this.clickViewOrder.emit();
  }

  public hasCollectionPin = computed<boolean>(() => {
    if (this.mode() == "collapsed") return false;
    return this.orderStatus() == "Ready" && this.isClickAndCollect();
  });

  public hasPaymentButton = computed<boolean>(() => {
    return this.orderStatus() == "Approved" && this.mode() !== "expanded";
  });

  async onClickCollectionPin(event: MouseEvent) {
    event.stopPropagation();
    const modal = await this.modalController.create({
      component: ClickAndCollectPage,
      canDismiss: true,
      presentingElement: await this.modalController.getTop() ? await this.modalController.getTop() : document.querySelector('ion-router-outlet')!,
      componentProps: {
        order: this.order(),
      }
    });
    await modal.present();
  }

  async onPay(event: MouseEvent) {
    event.stopPropagation();
    const order = this.order();
    if (!order || this.isPaying()) return;
    this.$amplitude.track('ORDER_PAY_START', { orderId: order.id });
    this.isPaying.set(true);
    const loader = await this.loadingController.create();
    await loader.present();
    try {
      const updatedOrder = await this.$cloud.markOrdersAsApprovedByCustomer(order.id);
      //this.order.set(updatedOrder);
      this.$amplitude.track('ORDER_PAY_SUCCESS', { orderId: order.id });
    } catch (error) {
      const message = (error as Parse.Error).message;
      this.$error.showToast({
        message,
        header: "Error paying for order",
        position: "top",
        duration: 2000,
      });
      this.$amplitude.track('ORDER_PAY_FAIL', { orderId: order.id, message });
    } finally {
      this.isPaying.set(false);
      await loader.dismiss();
    }
  }
}